<template>
  <ion-page id="practice-page">  
    <ion-content >
      <div id="the_board" :style="styleBoard">
        <CMChessboard :fen="fen" :validMoves="availableMoves" :orientation="orientation" 
                  :animateOnePly="animateFen" :hint="hintFen"
                  :setSquareColor="squareColorArray"
                  :boardSize="boardSize" @board-move="boardMove" />
      </div>

      <div v-show="showTree != 'hide'" id="the_tree"
                :class="{ 'text-dark' : isDark,'text-light' : !isDark }" 
                :style="styleTree">
        <RepTreeNewView 
                :treeDisplay="treeDisplay"
                :treeTrigger="treeTrigger" 
                @move-click="repTreeMoveClick"
                @branch-click="repTreeBranchClick"/> 
      </div>

      <div id="the_movehistory" 
                    :style="styleMoveHistory" 
                    :class="{ 'text-dark' : isDark, 'text-light' : !isDark }">
        <MoveHistory :moveHistoryStack="moveHistoryStack" :trigger="moveHistoryTrigger" />
        <b>{{ informationString }}</b>
      </div>

    </ion-content>
    <Toolbar :elements="toolbarElements" :trigger="toolbarTrigger"        
                          @toolbar-click="toolbarClick"/>
  </ion-page>
</template>

<script setup>

  import { IonPage, IonContent , modalController} from '@ionic/vue';

  import Toolbar from '@/components/Toolbar.vue';
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'
  import { t } from '@/composables/translateText.js'

  import RepTreeNewView from '@/components/RepTreeNewView.vue';

  import { Chess } from '@/ts/chess.ts'

  import CMChessboard from '@/components/CMChessboard.vue'
  import MoveHistory from '@/components/MoveHistory.vue'

  import { ref, watch } from 'vue'
  import { findToolbarElement, setToolbarElementDisabledFlag, setToolbarElementLabel} 
                from '@/composables/toolbarSupport.js'
  import { useGetData } from '@/composables/getData.js'
  import { useConnectionStore } from '@/stores/connectionStore';
  import { onMounted, onBeforeUnmount } from 'vue'

  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { availableWidthHeight, calculateChessBoardRect, isScreenPortraitOrientation } 
              from '@/composables/screenSize'
  import { useWindowSizeStore } from '@/stores/windowSizeStore';

  const TOOLBAR_AGAIN = 1
  const TOOLBAR_HINT = 0
  const TOOLBAR_PICK_BEGIN = 2

  
  const INITIAL_FEN = "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"

  const MOVEHISTORY_FONTSIZE = 16
  const MOVEHISTORY_HEIGHT =  MOVEHISTORY_FONTSIZE * 1.2 * 4 + 4

  const connectionStore = useConnectionStore()

  const isDark = ref(connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)
  const tourTrigger = ref(0)
  const showTour = ref(false)

  const showTree = ref("hide")

  const informationString = ref("")
  const squareColorArray = ref([])

  var isSubscriber = (connectionStore.getIsSubscriber == 1)

  const toolbarElements = ref([
    { index : TOOLBAR_HINT, label : t("Hint") , disabled : false, collapse:false},
    { index : TOOLBAR_AGAIN, label : t("Again") , disabled : true, collapse:false},
    { index : TOOLBAR_PICK_BEGIN, label : t("Pick Begin") , disabled : false, collapse:false},
  ])

  
  var repertoireRatingMin = 0
  var repertoireRatingMax = 0
  var repertoireName = ""
  var repertoirePercentage = 0
  const repertoireDescription = ref(t("Loading Repertoire Info") + "...")


  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                        height : windowSizeStore.getWindowSize['height'] })
  const boardSize = ref(100)

  
  const fen = ref(INITIAL_FEN)
  const initialFenid4 = "147869767"
  const initialFenidCombined = "0~" + initialFenid4

  const hintFen = ref([])
  const animateFen = ref([])

  var engineSkill = 20
  var engineToUse = "s"
  var playingWhite = 1
  var repertoireId = "0"
  var initialBoardSelectedInTree = true

  // # of moves in the practice run
  var targetMoveCount = 12
  var forceSwitchToEngineMoveNumber = 8

  var blankAppendTrigger = " "
  var orientation = ref("white")

  var checkMoveTreeFirst = 1

  var repTree = new Map()
  var displayTreeMap = new Map()
  var maxSpreadsheetColumnNumber = 0
  var spreadsheetColumnDefinitions = ref([])
  var treeDisplay = ref([])

  // what comes back from the server
  var mainMoves = []

  const availableMoves = ref([])
  var availableMovesAtGameStart = []
  var moveHistoryStackAtGameStart = []
  var hintAvailableAtGameStart = true
  var fenAtGameStart = INITIAL_FEN
  var gameStarted = false

  const moveHistoryStack = ref([])
  const moveHistoryTrigger = ref(0)
  const toolbarTrigger = ref(0)

  const treeTrigger = ref(0)

  var chess = new Chess()


  // tour stuff

  const sliceDiceTourInfo = {
    id : "opening_slicedice",
    video : "",
    elements : [
      { id: "", title: "Explore", text : "Why are we deciding our opening moves based on how the game ends 40 to 60 moves later?<br><br>That's an unfair burden for the game's first few moves."},
      { id: "", title: "There Is A Better Way!", text : "Chess Opening Mastery is a game changing, data driven approach to evaluating the game's first moves.<br><br>"},
      { id: "", title: "A Window into the Middle Game", text : "Chess Opening Mastery's revolutionary approach shows how the games are progressing in the middle game.<br><br>How? For every game, the Stockfish computer engine has been used to evaluate the position before white's moves 12, 15, 18 and 21. Stockfish scores each board as having an advantage for white (positive numbers) or an advantage for black (negative numbers)." },
      { id: "", title: "Stockfish Scores", text : "You probably know that a pawn is worth 1 point, a bishop/knight is worth 3 and a rook is 5 points. Stockfish reports back a position as being the equivalent of 'up' some number of pawns.<br><br>The bar chart shows that distribution for games start with the given move sequence."},
      { id: "", title: "The Game Collection", text : "The Lichess 'rapid' games were used to build the tree. With a unique twist - only games where the players are within the same 50 point 'bucket' are included.<br><br>Why? Because even a 50 point difference between two players gives the higher rated player a 6% win advantage which swamps any advantage that can be obtained by a particular opening."},
      { id: "the_tree", title: "The Graph", text : "The graph shows the distribution of the Stockfish engine scores for games where the checked move(s) was made.<br><br>For example, if '1.e4' is checked and the slider is at move 15 then the graph shows how well players who played 'e4' are doing in their games at move 15."},
      { id: "the_tree", title: "The Bars", text : "The bars show the percentage of games in each bucket. The buckets start on the left with black having already won the game by the future move number. Next to that is the percentage of games where black has a 'huge' advantage"},
      { id: "the_tree", title: "The Point Bars", text : "The next several bars break the stockfish engine scores into one point buckets.<br><br><i>(The 3 point bucket represents being up a bishop and the 1 point bucket represents being up by a pawn.)</i>"},
      { id: "the_tree", title: "The Middle &amp; White's Bars", text : "The center bar shows the percentage of games that are about even.<br><br>The bars to the right are white's mirroring the black buckets from 1 point through already won games."},
      { id: "the_toolbar", title: "Options", text : "Used to set the rating range and color."},
    ]
  }

  const tourInfo = ref(sliceDiceTourInfo)

  const styleTree = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleBoard = ref({
    position : "absolute",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  const styleMoveHistory = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px",
    "padding-top" : "10px",
    "font-size" : MOVEHISTORY_FONTSIZE + "px",
    "text-align" : "left",
    "line-height" : "1.2em",
    // "background-color" : "#8198bc",
    // "color" : "white"
  })

  doStartupCheck(false,afterStartupCallbackRoutine)

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function afterStartupCallbackRoutine(stuffChanged,errorProgram,errorEntry) {
    if (errorProgram != null)
      openErrorModal(errorProgram,errorEntry)
    if (connectionStore.getIsSubscriber != 1) {
      return
    }
    updateWidthHeights()
    getRepertoireInfo()
  }

  onMounted(() => {
  })

  function resetExplorerScreen() {
    // reset to beginning
    afterStartupCallbackRoutine(true,null,null)
  }

  watch(() => windowSizeStore.getWindowSize, () => {
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    updateWidthHeights()
    menuTrigger.value++
  })

  function updateStyleObjects(boundaries) {
    boardSize.value = boundaries['board'].height

    styleBoard.value.height = boundaries['board'].height + "px"
    styleBoard.value.width = boundaries['board'].width + "px"
    styleBoard.value.top = boundaries['board'].top + "px"
    styleBoard.value.left = boundaries['board'].left + "px"

    styleMoveHistory.value.height = boundaries['history'].height + "px"
    styleMoveHistory.value.width = boundaries['history'].width + "px"
    styleMoveHistory.value.top = boundaries['history'].top + "px"
    styleMoveHistory.value.left = boundaries['history'].left + "px"

    styleTree.value.height = boundaries['tree'].height + "px"
    styleTree.value.width = boundaries['tree'].width + "px"
    styleTree.value.top = boundaries['tree'].top + "px"
    styleTree.value.left = boundaries['tree'].left + "px"
  }


  function updateWidthHeights() {

    const availableSpace = availableWidthHeight()
    const isPortrait = isScreenPortraitOrientation(availableSpace)
    const boundaries = (isPortrait) ? calculatePortraitWidthHeights(availableSpace) :
                                      calculateLandscapeWidthHeights(availableSpace)
    updateStyleObjects(boundaries)
  }

  function calculatePortraitWidthHeights(availableSpace) {

    const availableTreeHeight = (showTree.value != "hide") ? availableSpace.height * .25 : 0

    const availableSpaceForBoard = { width : availableSpace.width ,
                  height : availableSpace.height - MOVEHISTORY_HEIGHT 
                          - availableTreeHeight,
                  isPortrait : true }

    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const moveHistoryPositionTop = boardPositionTop + boardHeight
    const moveHistoryPositionLeft = availableSpace.leftRightIndent
    const moveHistoryHeight = (showTree.value != "hide") ? MOVEHISTORY_HEIGHT/2 : MOVEHISTORY_HEIGHT
    const moveHistoryWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    const treePositionTop = moveHistoryPositionTop + moveHistoryHeight
    const treePositionLeft = moveHistoryPositionLeft
    const treeHeight = availableSpace.height - treePositionTop
    const treeWidth = moveHistoryWidth

    const result = { board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      history : {
        width : moveHistoryWidth,
        height : moveHistoryHeight,
        left : moveHistoryPositionLeft + availableSpace.leftIndent,
        top : moveHistoryPositionTop+ availableSpace.topIndent
      },
      tree : {
        width : treeWidth,
        height : treeHeight,
        left : treePositionLeft + availableSpace.leftIndent,
        top : treePositionTop+ availableSpace.topIndent
      },
    }
    return (result)
  }

  function calculateLandscapeWidthHeights(availableSpace) {

    const availableSpaceForBoard = { width : availableSpace.width - 150,
                height : availableSpace.height ,
                isPortrait : false }
    const boardDimensions = calculateChessBoardRect(availableSpaceForBoard,1.0)

    const boardHeight = boardDimensions.height
    const boardWidth = boardDimensions.width
    const boardPositionTop = boardDimensions.y
    const boardPositionLeft = boardDimensions.x + availableSpace.leftRightIndent

    const moveHistoryPositionTop = 0
    const moveHistoryPositionLeft = boardPositionLeft + boardWidth + availableSpace.leftRightIndent
    const moveHistoryHeight = MOVEHISTORY_HEIGHT * 2
    const moveHistoryWidth = availableSpace.width - moveHistoryPositionLeft

    const treePositionTop = moveHistoryPositionTop + moveHistoryHeight
    const treePositionLeft = moveHistoryPositionLeft
    const treeHeight =  availableSpace.height - treePositionTop
    const treeWidth = moveHistoryWidth

    const result = { 
      board : {
        width : boardWidth,
        height : boardHeight,
        left : boardPositionLeft + availableSpace.leftIndent,
        top : boardPositionTop+ availableSpace.topIndent
      },
      history : {
        width : moveHistoryWidth,
        height : moveHistoryHeight,
        left : moveHistoryPositionLeft + availableSpace.leftIndent,
        top : moveHistoryPositionTop+ availableSpace.topIndent
      },
      tree : {
        width : treeWidth,
        height : treeHeight,
        left : treePositionLeft + availableSpace.leftIndent,
        top : treePositionTop+ availableSpace.topIndent
      }
    }
    return (result)
  }

  // End for Slice & Dice

  function incrementAppendTrigger() {
    if (blankAppendTrigger.length > 64)
      blankAppendTrigger = ""
    blankAppendTrigger += " "
  }

  function tourDone() {
    showTour.value = false
  }

  function toolbarClick(index) {

    const element = findToolbarElement(toolbarElements.value,index)
    if (element.disabled)
      return

    switch (index) {
      case TOOLBAR_AGAIN :
        squareColorArray.value = []
        informationString.value = ""
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_PICK_BEGIN,false)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,false)
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,!hintAvailableAtGameStart)
        toolbarTrigger.value++
        showTree.value = "hide"
        updateWidthHeights()
        availableMoves.value = availableMovesAtGameStart
        moveHistoryStack.value = JSON.parse(JSON.stringify(moveHistoryStackAtGameStart));
        moveHistoryTrigger.value++
        gameStarted = false
        fen.value = fenAtGameStart
        squareColorArray.value = [ ]
        break
      case TOOLBAR_PICK_BEGIN :
        informationString.value = ""
        squareColorArray.value = []

        if (showTree.value == "hide") {
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)
          setToolbarElementLabel(toolbarElements.value,TOOLBAR_PICK_BEGIN,t("Close Pick"))
          showTree.value = "show"
        }
        else {
          setToolbarElementLabel(toolbarElements.value,TOOLBAR_PICK_BEGIN,t("Pick Begin"))
          setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,!hintAvailableAtGameStart)
          showTree.value = "hide"
        }
        toolbarTrigger.value++
        updateWidthHeights()
        break
      case TOOLBAR_HINT :
        hintFen.value = [fen.value, availableMoves.value[0].fen]
        break    
    }
  }

  // https://stackoverflow.com/questions/951021/what-is-the-javascript-version-of-sleep
  // sleep time expects milliseconds
  function promiseSleep(time) {
    return new Promise((resolve) => setTimeout(resolve, time));
  }

  function makePlayerMoveOnlyAvailableMove(parentFenidCombined,parentFen) {
    availableMoves.value = []

    const toArray = repTree.get(parentFenidCombined)

    // no player move in tree after last opponent's move. Need to load from server
    if (toArray == null) {
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)
      toolbarTrigger.value++
      getEngineMove(parentFen,false)
      return (false)
    }

    const oneAvailableMove = { 
      from : toArray[0].fromSquare, 
      to : toArray[0].toSquare,
      san : toArray[0].san,
      fen : toArray[0].fen,
      combined : toArray[0].to,
      moveSource : "repertoire",
      moveScoreType : 0,
      moveScoreValue : 0
    }

    availableMoves.value.push(oneAvailableMove)

    return (true)
  }

  function addPlayerMoveToHistoryStack(playerMove) {

  }

  function addMoveToHistoryStack(san,combined,fromSquare,toSquare,
            moveSource,moveScoreType,moveScoreValue,promotionCharacter) {

    const oneMove = {
            combined : combined, 
            san : san, 
            fromSquare : fromSquare,
            toSquare : toSquare,
            moveSource : moveSource,
            moveScoreType : moveScoreType,
            moveScoreValue : moveScoreValue,
            promotionCharacter : promotionCharacter
            }

    moveHistoryStack.value.push(oneMove)
    moveHistoryTrigger.value++
    return (oneMove)
  }

  function addOpponentMoveToHistoryStack(opponentMove) {
    addMoveToHistoryStack(opponentMove.san,opponentMove.to,
                  opponentMove.fromSquare,opponentMove.toSquare,"repertoire",0,0,"")
    updateTreeView()

    const currentFen = fen.value
    const newFen = opponentMove.fen

    // wait a bit before animating oppponent move
    promiseSleep(250).then(() => {
      // Do something after the sleep!
      animateFen.value = [currentFen, newFen]
      promiseSleep(250).then(() => {
        fen.value = newFen
      });
    });
  }

  // randomly select an opponent move based on popularity
  function randomOpponentMove(parentFenidCombined,fromBoardMove) {
    const move = repTree.get(parentFenidCombined)
    //console.log("fenid and move",parentFenidCombined,move)
    if (move == null) {
        if (!fromBoardMove)
          hintAvailableAtGameStart = false
        setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,true)
        toolbarTrigger.value++
        getEngineMove(fen.value,true)
        return
    }

    var totalPercentage = 0
    for (var i=0; i < move.length; i++)
      totalPercentage += move[i].move_percentage

    const randomNumber = Math.random()
    const targetPercentage = totalPercentage * randomNumber
    var totalPercentage = 0
    var targetIndex = 0
    for (var i=0; i < move.length; i++) {
      totalPercentage += move[i].move_percentage
      if (totalPercentage > targetPercentage) {
        targetIndex = i
        break
      }
    }

    if (!fromBoardMove)
          hintAvailableAtGameStart = true

    const opponentMove = move[targetIndex]
    addOpponentMoveToHistoryStack(opponentMove)

    makePlayerMoveOnlyAvailableMove(opponentMove.to,opponentMove.fen)
  }

  function boardMove(arrayIndex) {

    if (!gameStarted) {
      fenAtGameStart = fen.value
      availableMovesAtGameStart = availableMoves.value
      //moveHistoryStackAtGameStart = moveHistoryStack.value
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_PICK_BEGIN,true)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,true)
      setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_HINT,!hintAvailableAtGameStart)
      setToolbarElementLabel(toolbarElements.value,TOOLBAR_PICK_BEGIN,t("Pick Begin"))
      toolbarTrigger.value++
      gameStarted = true

      informationString.value = ""

      if (showTree.value == "show") {
        showTree.value = "hide"
        updateWidthHeights()
      }
    }

    fen.value = availableMoves.value[arrayIndex].fen
    
    const combined = availableMoves.value[arrayIndex].combined
    const san = availableMoves.value[arrayIndex].san
    const fromSquare = availableMoves.value[arrayIndex].from
    const toSquare = availableMoves.value[arrayIndex].to
    const scoreType = availableMoves.value[arrayIndex].moveScoreType
    const scoreValue = availableMoves.value[arrayIndex].moveScoreValue
    const promotionCharacter = availableMoves.value[arrayIndex].promotionCharacter
    const moveSource = availableMoves.value[arrayIndex].moveSource

    addMoveToHistoryStack(san,combined,fromSquare,toSquare,moveSource,scoreType,scoreValue,promotionCharacter)

    randomOpponentMove(combined,true)
    //getAvailableMoves(combined,previousMovePercentage,"update_repertoire")
  }

  function debounce(callback, interval=500) {
    let debounceTimeoutId;

    return function(...args) {
      clearTimeout(debounceTimeoutId);
      debounceTimeoutId = setTimeout(() => callback.apply(this, args), interval);
    };
  }

  // this version recursively opens as many branches as the user has set to open
  function repTreeBranchClick(branchElement) {
    //console.log("**** in branch click",branchElement)
    const move = branchElement.move
    move.branchIsOpen = !move.branchIsOpen
    buildRecursiveDisplayTreeView(initialFenidCombined,!playingWhite,0,0)

  }

  function clearTreeDisplaySelectedFlag() {
    for (var i=0; i < treeDisplay.value.length; i++) {
      for (var j=0; j < treeDisplay.value[i].moves.length; j++)
        treeDisplay.value[i].moves[j].selected = false
    }
  }

  function repTreeMoveClick(branchElement) {
    //console.log("**** in move click",branchElement)
     
    clearTreeDisplaySelectedFlag()
  
    branchElement.selected = true
    treeTrigger.value++

    const move = branchElement.move

    const combineds = move.fenidCombinedStackString.split(",")
    //console.log("*** FENIDS in rep tree move click",combineds)

    moveHistoryStack.value = []

    // check for initial board
    if (combineds[0] == "0~0") {
      initialBoardSelectedInTree = true
      fen.value = INITIAL_FEN
      informationString.value = ""
      if (playingWhite == 1)
        makePlayerMoveOnlyAvailableMove(initialFenidCombined,INITIAL_FEN)
      else
        randomOpponentMove(initialFenidCombined,false)
      moveHistoryTrigger.value++
      return
    }

    initialBoardSelectedInTree = false
    for (var i=0; i < combineds.length - 1; i++) {
      const move = repTree.get(combineds[i])
      if (move == null)
        break

      var oneMove = null
      for (var j=0; j < move.length; j++)
        if (move[j].to == combineds[i+1]) {
          fen.value = move[j].fen
          const san = move[j].san
          const percent = move[j].move_percentage

          oneMove = addMoveToHistoryStack(san,move[j].to,move[j].fromSquare,move[j].toSquare,
                        "start",0,0,"")
          break;
        }
    }

    moveHistoryStackAtGameStart = JSON.parse(JSON.stringify(moveHistoryStack.value));

    moveHistoryTrigger.value++
    const isWhiteMove = (combineds.length % 2)

    if (playingWhite == isWhiteMove)
      makePlayerMoveOnlyAvailableMove(oneMove.combined,oneMove.fen)
    else
      randomOpponentMove(oneMove.combined,false)
  }

  function findMoveInTreeRecursive(parentFenidCombined,moveCombinedString) {
    const moves = repTree.get(parentFenidCombined)
    var found = false

    if (moves == null)
      return (false)

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
        const toCombined = moves[i].to

        if (moves[i].fenidCombinedStackString == moveCombinedString) {
          return (true)
        }
       
        const found = findMoveInTreeRecursive(moves[i].to,moveCombinedString)

        // open up all parent branches
        if (found) {
          if (moves[i].canBranchAfterThisMove)
            moves[i].branchIsOpen = true
          return (true)
        }
      }
    return (found)
  }

  function buildMoveHistoryCombinedString() {
    var moveCombineds = [ initialFenidCombined ]
    for (var i = 0; i < moveHistoryStack.value.length; i++)
      moveCombineds.push(moveHistoryStack.value[i].combined)
    const moveCombinedString = moveCombineds.join(",")
    return (moveCombinedString)
  }

  // find boardmove in repertoire tree
  function findMoveInTree() {
    // get the move stack to find the move in the tree
    const moveCombinedString = buildMoveHistoryCombinedString()
    findMoveInTreeRecursive(initialFenidCombined,moveCombinedString)
  }

   // is boardmove  already in the visible tree?
   //    if yes, set selected and return true.
   function findMoveInTreeDisplay() {
    // get the move stack to find the move in the tree
    const moveCombinedString = buildMoveHistoryCombinedString()

    if (moveCombinedString == initialFenidCombined) {
      initialBoardSelectedInTree = true
      if (treeDisplay.value.length > 0)
        treeDisplay.value[0].moves[0].selected = true
      return (false)
    }

    for (var i=0; i < treeDisplay.value.length; i++) {
      for (var j=0; j < treeDisplay.value[i].moves.length; j++) {
        if (treeDisplay.value[i].moves[j].move.fenidCombinedStackString == moveCombinedString) {
          //console.log("FOUND STRING IN TREE ",treeDisplay.value[i].moves[j])
          treeDisplay.value[i].moves[j].selected = true
          return (true)
        }
      }
    }
    return (false)
  }

  // build repertoire tree display. Allow for multiple open branches
  function buildRecursiveDisplayTreeView(parentFenidCombined,parentIsOpen,ply,indentLevel) {
    if (parentFenidCombined == initialFenidCombined) {
      treeDisplay.value = []
      treeDisplay.value.push({index:treeDisplay.value.length,indentLevel : indentLevel,moves : []})

      const oneMove = { 
                from : 0, 
                to : initialFenidCombined, 
                move_percentage : 0,
                san : "Init", 
                white : 0,
                count : 0,
                comment : "",
                fen : INITIAL_FEN,
                fenidCombinedStackString : "0~0," + initialFenidCombined
              }

      const oneTreeDisplayMove = {
              index : 0,
              ply : -1,
              indentLevel : 0,
              isOpen : false,
              selected : initialBoardSelectedInTree,
              parentIsOpen : false,
              move : oneMove
      }
      treeDisplay.value[0].moves.push(oneTreeDisplayMove)

    }

    const moves = repTree.get(parentFenidCombined)
    if (moves == null)
      return

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
      const toCombined = moves[i].to
  
      const oneTreeDisplayMove = {
              index : treeDisplay.value[treeDisplay.value.length-1].moves.length,
              ply : ply,
              indentLevel : indentLevel,
              isOpen : false,
              selected : false,
              parentIsOpen : parentIsOpen,
              move : moves[i]
      }
      if (parentIsOpen) {
        treeDisplay.value.push({index:treeDisplay.value.length,indentLevel : indentLevel+1,moves : []})
      }
      if (parentIsOpen || i == 0) {
        treeDisplay.value[treeDisplay.value.length-1].moves.push(oneTreeDisplayMove)
        buildRecursiveDisplayTreeView(moves[i].to,moves[i].branchIsOpen,ply+1,
                        (parentIsOpen) ? indentLevel+1: indentLevel)
      }
    }

    if (parentFenidCombined == initialFenidCombined) {
      treeTrigger.value++
    }
  }

  // walk entire repertoire building the list of combineds to get to this move
  function buildRepTreeFenidStack(parentFenidCombined,fenidCombinedStackString) {

    const moves = repTree.get(parentFenidCombined)
    if (moves == null)
      return

    const nextMoves = (moves.length != 1) ? null : repTree.get(moves[0].to)
    const canBranch = (nextMoves != null && nextMoves.length > 1)

    // recurse on each move from the parent
    for (var i=0; i < moves.length; i++) {
      moves[i].canBranchAfterThisMove = canBranch
      moves[i].branchIsOpen = false
      moves[i].fenidCombinedStackString = fenidCombinedStackString + "," + moves[i].to
      buildRepTreeFenidStack(moves[i].to,moves[i].fenidCombinedStackString)
    }
  }

  function updateTreeView() {
    // clear the previous selection and see if the new move is already visible
    clearTreeDisplaySelectedFlag()

    const moveIsVisible = findMoveInTreeDisplay()

    // move isn't visible so find it in repertoire and open up parents
    if (moveIsVisible == false) {
      findMoveInTree()
      buildRecursiveDisplayTreeView(initialFenidCombined,!playingWhite,0,0 )
      findMoveInTreeDisplay()
    }
    treeTrigger.value++
  }

  // right now, color both sides - green (player) and red (opponet)
  function indicatePlayedEnough(endingFen) {

    setToolbarElementDisabledFlag(toolbarElements.value,TOOLBAR_AGAIN,false)
    toolbarTrigger.value++

    squareColorArray.value = [ ]

    //console.log("indicate played enough",endingFen)

    chess.load(endingFen)
    const board = chess.board()

    for (var row=0; row < 8; row++) {
      for (var col=0; col < 8; col++) {
        if (board[row][col] == null)
          continue;
        const onePiece = board[row][col]
        if (onePiece.type != 'p')
           continue
        var color = "lightgreen"

        if (playingWhite == 1 && onePiece.color == 'b')
          color = "red"
        if (playingWhite == 0 && onePiece.color == 'w')
          color = "red"
        /*
        if (playingWhite == 1 && onePiece.color == 'b')
          continue
        if (playingWhite == 0 && onePiece.color == 'w')
          continue
        */
        //if (color == "red")
        squareColorArray.value.push({ square : onePiece.square , color : color})

      }
    }
  }

  function processCoverageJsonResponse (json) {
    //console.log(" in coverage json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    const next_program = json['next_program']

    const froms = json['froms'].split(",")
    const tos = json['tos'].split(",")
    const board_froms = json['board_froms'].split(",")
    const board_tos = json['board_tos'].split(",")
    const sans = json['sans'].split(",")
    const whites = json['whites'].split(",")
    const counts = json['counts'].split(",")
    const fens = json['fens'].split(",")
    const comments = json['comments'].split("\t")
    const move_percentages = json['move_percentage'].split(",")

    const count = Number(json['count'])

    repTree.clear();

    for (var i=0; i < count; i++) {
      const oneMove = { 
                from : froms[i], 
                to : tos[i], 
                fromSquare : board_froms[i],
                toSquare : board_tos[i],
                promotion : 0,
                move_percentage : Math.trunc(Number(move_percentages[i]) * 1000000),
                san : sans[i], 
                comment : (comments[i] == undefined) ? "" : comments[i],
                white : whites[i],
                count : counts[i],
                fen : fens[i],
                fenidCombinedStackString : ""
              }
  
      if (repTree.has(froms[i]) == false)
        repTree.set(froms[i],[])
      const toArray = repTree.get(froms[i])
      toArray.push(oneMove)
    }

    
    buildRepTreeFenidStack(initialFenidCombined,initialFenidCombined)
    //console.log("**** REPTREE ",repTree)

    updateTreeView()

    if (playingWhite == 1) {
      makePlayerMoveOnlyAvailableMove(initialFenidCombined,INITIAL_FEN)
    }
    else
      randomOpponentMove(initialFenidCombined,false)

  }

  function getCoverage() {

    if (repertoireId == "0")
      return

    var formData = {
      playing_white : playingWhite,
      repid : repertoireId,
      percentage : repertoirePercentage,
      min_rating : repertoireRatingMin,
      max_rating : repertoireRatingMax - 1
    }

    //console.log("coverage postdata ",formData)
    useGetData("opening_coverage2.php", formData, processCoverageJsonResponse)
    
  }

  function processEngineMoveJsonResponse(json) {
    //console.log(" in engine move callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    checkMoveTreeFirst = json['check_tree_first']
    const engineMoveCount = json['eng_count']
    const engineFen = json['eng_fens']
    const engineSan = json['eng_sans']
    const engineFromSquare = json['eng_froms']
    const engineToSquare = json['eng_tos']
    const enginePromotion = json['eng_promotion']

    const engineScoreType = json['engine_score_type']
    const engineScoreValue = json['engine_score_value']

    var engineScore = json['engine_score']
    var engineScoreForeign = ""
    const engineSource = json['engine_source']

    if (engineMoveCount > 0) {
      addMoveToHistoryStack(engineSan,"0~0",engineFromSquare,engineToSquare,
                        engineSource,engineScoreType,engineScoreValue,enginePromotion)
      animateFen.value = [fen.value , engineFen]
    }

    const playerInCheck = json['player_in_check']
    var playerCount = json['play_count']
    const playerFens = json['play_fens'].split(",")
    const playerFromSquares = json['play_froms'].split(",")
    const playerToSquares = json['play_tos'].split(",")
    const playerSans = json['play_sans'].split(",")
    const playerCastles = json['play_castle'].split(",")
    const playerChecks = json['play_check'].split(",")
    const playerPassants = json['play_passant'].split(",")
    const playerPromotions = json['play_promotion'].split(",")

    const playerScoreType = json['player_score_type']
    const playerScoreValue = json['player_score_value']

    const playerScore = json['player_score']
    var gameOver = Math.floor(json['game_is_over'])

    // if player has no moves, game is over
    if (gameOver == 0 && playerCount == 0) {
      gameOver = 1
      engineScore = (playerInCheck == 1) ? "You Got Checkmated" : "You Checkmated!"
      engineScoreForeign = (playerInCheck == 1) ? t("You Got Checkmated") : t("You Checkmated") + "!"
    }

    if (gameOver > 0) {
      engineScoreForeign = ""
      switch (engineScore) {
        case "You Got Checkmated" :
          break
        case "You Checkmated!" :
          break 
        case "Stalemate" :
        case "50 Move Rule" :
        case "Insufficent Material" :
          engineScore = "Stalemate"
          engineScoreForeign = t("Stalemate")
          break 
        case "White Checkmate" :
          engineScore = (orientation.value == "white") ? "You Checkmated!" : "You Got Checkmated"
          engineScoreForeign = (orientation.value == "white") ? t("You Checkmated") + "!" : t("You Got Checkmated")
          break
        case "Black Checkmate" :
          engineScore = (orientation.value == "black") ? "You Checkmated!" : "You Got Checkmated"
          engineScoreForeign = (orientation.value == "black") ? t("You Checkmated") + "!" : t("You Got Checkmated")
          break
      }
      if (engineScore == "You Got Checkmated")
        informationString.value +=  t("You Got Checkmated")
      else
      if (engineScore == "Stalemate")
        informationString.value += t("Stalemate") 
      else
      if (engineScore == "You Checkmated!") {
        informationString.value +=  t("You Checkmated") + "!" 
      }

      indicatePlayedEnough(engineFen)
      savePracticeAttempt()
    }
    else
    if (moveHistoryStack.value.length >= targetMoveCount * 2 && engineMoveCount > 0) {
      // has this played out far enough?
      // AND ending on an engine move?

      indicatePlayedEnough(engineFen)
      playerCount = 0

      // save result to database
      // set some flag
      informationString.value = "*** " + t("Move") + " : " + targetMoveCount + " " + t("Score") + ": " + engineScore
      savePracticeAttempt()
    }

    availableMoves.value = []
    for (var i=0; i < playerCount; i++) {
      const oneMove = { fen : playerFens[i],
                          from : playerFromSquares[i],
                          to : playerToSquares[i],
                          san : playerSans[i],
                          castle : playerCastles[i],
                          check : playerChecks[i],
                          passant : playerPassants[i],
                          promotionCharacter : playerPromotions[i],
                          moveScoreType : playerScoreType,
                          moveScoreValue : playerScoreValue,
                          moveSource : "player",
                          combined : "0~0" }
        availableMoves.value.push(oneMove)
    }

  }

  function getEngineMove(parentFen,isOpponentMove) {

    if (forceSwitchToEngineMoveNumber * 2 <= moveHistoryStack.value.length - 1)
      checkMoveTreeFirst = 0

    clearTreeDisplaySelectedFlag()
    availableMoves.value = []

    var formData = {
      fen : parentFen,
      is_opponent_move : (isOpponentMove) ? "1" : "0",
      playing_white : playingWhite,
      skill : engineSkill,
      engine_to_use : engineToUse,
      check_game_collection_first : checkMoveTreeFirst,
      min_rating : repertoireRatingMin,
      max_rating : repertoireRatingMax - 1
    }
    //console.log("in get engine move ",formData)
    useGetData("opening_engine_move.php", formData,processEngineMoveJsonResponse)
  }

  function processSavePracticeAttemptJsonResponse(json) {
    //console.log(" in engine move callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }
  }

  function savePracticeAttempt() {

    var combineds = []
    var froms = []
    var tos = []
    var promotions = []
    var sans = []
    var scoreTypes = []
    var scoreValues = []
    var sources = []

    for (var i=0; i < moveHistoryStack.value.length; i++) {
      const oneMove = moveHistoryStack.value[i]
      combineds.push(oneMove.combined)
      froms.push(oneMove.fromSquare)
      tos.push(oneMove.toSquare)
      promotions.push(oneMove.promotionCharacter)
      sans.push(oneMove.san)
      scoreTypes.push(oneMove.moveScoreType)
      scoreValues.push(oneMove.moveScoreValue)
      sources.push(oneMove.moveSource.substring(0,1))
    }

    var formData = {
      repid : repertoireId,
      skill : engineSkill,
      engine : engineToUse,
      playing_white : playingWhite,
      min_rating : repertoireRatingMin,
      max_rating : repertoireRatingMax-1,
      percentage : repertoirePercentage,

      combineds : combineds.join(","),
      sources : sources.join(","),
      froms : froms.join(","),
      tos : tos.join(","),
      promotions : promotions.join(","),
      sans : sans.join(","),
      types : scoreTypes.join(","),
      scores : scoreValues.join(",")
    }
    //console.log("in save practice attempt ",formData)
    useGetData("opening_write_practice_attempt.php", formData,processSavePracticeAttemptJsonResponse)
  }

  function processRepertoireInfoJsonResponse (json) {
    //console.log(" in initial json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    repertoireId = json['repid']
    if (repertoireId != 0) {
      repertoireRatingMax = Number(json['rating_max']) + 1
      repertoireRatingMin = Number(json['rating_min'])
      repertoirePercentage = json['percentage']
      repertoireName = json['name']

      playingWhite = parseInt(json['playing_white'])
      if (playingWhite == 0)
        orientation.value = "black"

      repertoireDescription.value = t("Practice") + ": " + repertoireName + ", " + t("Rating") + ":" + repertoireRatingMin + "-" +
                repertoireRatingMax + " " + t("with") + " " + repertoirePercentage + "% " + t("Filter")
    }
    else
      repertoireDescription.value = "***** Use the Repertoire Manager to Select Repertoire ******"

    informationString.value = repertoireDescription.value

    getCoverage()
    menuTrigger.value++
  }

  function getRepertoireInfo() {
    var formData = { }
    useGetData("opening_repertoire_info.php", formData, processRepertoireInfoJsonResponse)
  }

</script>

<style scoped>

#explorer-container {
  position : relative;
}

.reptree-container-background-dark {
  background-color: #403726;
  z-index : 0;
}

.reptree-container-background-light {
  background-color : #ebf2f2;
  z-index: 0;
}

</style>